/**
 * The three ways a layer can be shared — `docs/property-maps-v2.md` §5.5.
 *
 * One component, used by the sharing dialog and by *New layer*, because the
 * three options and their sentences are the feature's central decision and
 * two copies of them is two chances to describe it differently.
 *
 * **All crews is stored as `all`, never as a list** (§12 rule 4): a division
 * added next year is included without anybody revisiting the layer. Picking
 * *Specific divisions* with nothing ticked is not a choice, so Save is
 * disabled rather than quietly meaning nothing.
 */
import { LAYER_PALETTE } from '@rowted/shared'
import type { Audience } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Checkbox } from '../../components/primitives.js'
import type { Division } from './audience-chip.js'

const OPTIONS: { value: Audience; label: string; body: string }[] = [
  {
    value: 'all',
    label: 'All crews',
    body: 'Default. Every crew that has a visit at this property, in any division.'
      + ' New crews and divisions are included automatically.',
  },
  {
    value: 'divisions',
    label: 'Specific divisions',
    body: 'Only crews in the divisions you tick. Everyone else sees the layers they’re'
      + ' allowed to and nothing of this one.',
  },
  {
    value: 'me',
    label: 'Only me',
    body: 'A scratch layer for planning. It never goes to a visit or the crew app.',
  },
]

export function AudienceField({
  audience, divisionIds, divisions, crewCounts, onChange,
}: {
  audience: Audience
  divisionIds: string[]
  divisions: Division[]
  /** How many crews each division has, for `Snow 4 crews`. */
  crewCounts?: Record<string, number>
  onChange: (next: { audience: Audience; divisionIds: string[] }) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      {OPTIONS.map((option) => {
        const on = audience === option.value
        return (
          <div
            key={option.value}
            className={cn(
              'rounded-field border p-3',
              on ? 'border-accent-line bg-accent-soft/40' : 'border-line',
            )}
          >
            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="radio"
                name="audience"
                checked={on}
                onChange={() => onChange({ audience: option.value, divisionIds })}
                className="mt-[3px] h-3.5 w-3.5 accent-[rgb(var(--color-accent))]"
              />
              <span className="flex flex-col gap-1">
                <b className="text-body">{option.label}</b>
                <span className="text-cell leading-[1.45] text-text-muted">{option.body}</span>
              </span>
            </label>

            {on && option.value === 'divisions' && (
              <div className="mt-2.5 flex flex-col gap-1 pl-[26px]">
                {divisions.length === 0 && (
                  <span className="text-cell text-text-muted">
                    There are no active divisions to pick.
                  </span>
                )}
                {divisions.map((division) => (
                  <Checkbox
                    key={division.id}
                    className="py-1"
                    checked={divisionIds.includes(division.id)}
                    onChange={(event) => onChange({
                      audience: 'divisions',
                      divisionIds: event.target.checked
                        ? [...divisionIds, division.id]
                        : divisionIds.filter((id) => id !== division.id),
                    })}
                    label={(
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden
                          className="h-2 w-2 shrink-0 rounded-chip"
                          style={{ background: division.colour || LAYER_PALETTE.slate }}
                        />
                        {division.name}
                        {crewCounts?.[division.id] !== undefined && (
                          <span className="text-text-muted">
                            {crewCounts[division.id]}
                            {crewCounts[division.id] === 1 ? ' crew' : ' crews'}
                          </span>
                        )}
                      </span>
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Whether a choice can be saved — §5.5: divisions with nothing ticked cannot. */
export const audienceReady = (audience: Audience, divisionIds: string[]): boolean =>
  audience !== 'divisions' || divisionIds.length > 0
